import { c as _c } from "react/compiler-runtime";
import { ActionIcon, Button, CheckIcon, Chip, Skeleton, Switch, Tooltip, useSafeMantineTheme, Transition } from '@mantine/core';
import { useFullscreen } from '@mantine/hooks';
import { MdFullscreen, MdHome } from 'react-icons/md';
import { useQueryString } from 'utils/useQueryString';
import { mapToObj, pick, pipe, sortBy } from 'remeda';
import { HiArrowCircleUp, HiArrowRight, HiChevronDown, HiChevronUp, HiDownload, HiX } from 'react-icons/hi';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { SubqItem, useSubqQuery } from './subq';
import { QuestionCfg, Forecast, QuestionResultsLayout as QR, Subqs, questionColors } from 'shared/forecast';
import { compactObj, groupMapObj, isNullOrEmpty } from 'shared/collection';
import { doSafe } from 'shared/promise';
import { uri } from 'shared/uri';
import { capitalize } from 'shared/text';
import { useMemoValue, MasonryGrid, SErrorBoundary, useWindowDim } from 'shared/ui';
import { resultColorFn, QuestionResultsProps, QuestionResults } from 'shared/forecast/results/QuestionResults';
import { makeQuestionForecastExport } from './questionCsv';
import { downloadBlob } from 'utils/download';
import { useQuestionContext, zQuestionQuery } from './questionComponentTypes';
import { useForecastNotes, getNoteCustom } from './useForecastNotes';
import { QuestionTimeline, SharePublication, TimeComparisonSelect, TsLatest, useTimeline } from './QuestionTimeline';
import { CardCollapsable } from 'components/Card';
import { SColorInput } from 'components/form/SColorInput';
import { usePortal } from 'components/portal/portal';
import { ComboMultiInput } from 'components/form/ComboMulti';
import { P, match } from 'ts-pattern';
import { env } from 'utils/env';
import { SubqTypeIcon } from './Subqs';
import { twMerge } from 'tailwind-merge';
import { ProfileFilter } from 'components/portal/Profile';
import { InviteLink, qrImageUrl, useInviteLink } from 'components/portal/admin/inviteLinks';
import { InfoLabel } from 'components/Info';
import { ResultsQuery } from 'shared/forecast/results';
import { VscLayoutSidebarRight } from 'react-icons/vsc';
import { CopyLink } from 'components/form/CopyLink';
import z from 'zod';
import { usePublishedUrl } from './QuestionPublish';
import { useQuestionForm, useQuestionQuery } from './useQuestions';
import { useForecasts } from './useForecasts';
import { Workspace as W } from 'components/portal/workspace';
import { NotNull } from 'shared/type';
const useQuestionResultsSection = () => {
  const {
    question_id,
    readonly
  } = useQuestionContext()!;
  const {
    form: qForm
  } = useQuestionForm({
    question_id,
    pick: cfg => pick(cfg, ['publish'])
  });
  const {
    data: questionRaw
  } = useQuestionQuery({
    question_id
  });
  const {
    query,
    setQuery
  } = useQueryString(zQuestionQuery);

  // so many things depend on question, we wan to use the stored row for most things except when you want to see edits immediately
  const question = useMemoValue(useMemo(() => {
    return questionRaw?.ok && qForm.values != null ? {
      id: question_id,
      ...questionRaw.cfg,
      ...pick(qForm.values, ['publish'])
    } as QuestionCfg & {
      id: string;
    } : undefined;
  }, [questionRaw, qForm.values]));
  const {
    data: subqsRaw
  } = useSubqQuery({
    question_id,
    status: ['approved']
  });
  const {
    groups: groups_0,
    subqs: subqs_0
  } = useMemo(() => {
    if (!subqsRaw || !question) return {};
    const groups = Subqs.group(subqsRaw, {
      question
    }).map(g => ({
      ...g,
      subqs: g.subqs.filter(s => s.cfg.status == 'approved')
    }));
    const subqs = groups.flatMap(g_0 => g_0.subqs);
    return {
      groups,
      subqs
    };
  }, [subqsRaw, question]);
  const timestamps = doSafe(query.compare_ts?.map(t => ({
    ts: t == TsLatest ? undefined : Number.parseInt(t),
    tsName: question?.publish.publications[t.toString()]?.name
  })), t_0 => t_0.length == 0 || t_0.length == 1 && t_0[0]?.ts == null ? undefined : sortBy(t_0, [t_1 => t_1.ts ?? Number.MAX_SAFE_INTEGER, 'desc']) // if its just latest, re-use the existing forecasts from useQ
  );
  const uForecasts = useForecasts({
    filter: {
      question_id,
      timestamps: timestamps?.map(t_2 => t_2.ts)
    }
  });
  const {
    forecastGroups
  } = uForecasts;
  const uForecastNotes = useForecastNotes({
    question_id,
    qForm
  });
  const forecasterCounts = useMemo(() => {
    const sqIds = subqs_0?.filter(s_0 => isNullOrEmpty(query.showQuestions) || query.showQuestions.includes(s_0.id))?.map(s_1 => s_1.id) ?? [];
    // count the number of forecast groups for each forecaster (to match participation counts)
    return groupMapObj(forecastGroups.flatMap(g_1 => (g_1.forecasts ?? []).filter(f => Forecast.shouldShow(f) && f.created_by != null && (f.subquestion_id == null || sqIds.includes(f.subquestion_id)))), c => c.created_by!, g_2 => g_2.length);
  }, [forecastGroups, subqs_0, query.showQuestions, query.compare_ts]);
  return {
    question_id,
    question,
    subqs: subqs_0,
    groups: groups_0,
    uForecastNotes,
    query,
    setQuery,
    uForecasts,
    readonly,
    timestamps,
    forecasterCounts
  };
};
type UseQuestionResultsSection = ReturnType<typeof useQuestionResultsSection>;
export const QuestionResultsSection = ({}: {}) => {
  const {
    profile
  } = usePortal();
  const {
    w: windowWidth
  } = useWindowDim();
  const {
    ref,
    toggle,
    fullscreen
  } = useFullscreen();
  const [showResultsPanelState, setShowResultsPanel] = useState<boolean>();
  const {
    question,
    subqs,
    groups,
    uForecastNotes,
    query,
    uForecasts,
    readonly,
    timestamps,
    forecasterCounts
  } = useQuestionResultsSection();
  const {
    getForecasts,
    forecastsLoading,
    forecastsFetching
  } = uForecasts;
  const showResultsPanel = showResultsPanelState ?? (windowWidth > 1200 && !readonly);
  const theme = useSafeMantineTheme();
  const [joinExpand, setJoinExpand] = useState(false);
  const noteRender = useMemo(() => ({
    noteCustom: uForecastNotes.isQuestionEditor ? getNoteCustom(uForecastNotes) as QuestionResultsProps['noteCustom'] : undefined
  }), [uForecastNotes.isQuestionEditor, uForecastNotes.question_id]);
  const uWorkspace = W.useAdminWorkspace({
    id: profile?.workspace_id,
    enabled: profile?.hasRole('admin')
  });
  const inviteLinkMain = useInviteLink({
    uWorkspace
  });
  return match({
    question,
    forecastsLoading,
    profile
  }).with({
    question: P.not(P.nullish),
    forecastsLoading: false
  }, ({
    question: question_0
  }) => {
    const questionResultsProps = {
      question: question_0,
      timestamps: timestamps,
      getForecasts: getForecasts!,
      subqs: subqs as SubqItem[],
      theme,
      subquestionHighlightId: query.subquestion,
      noteCustom: noteRender.noteCustom,
      forecastsFetching,
      ...query
    } satisfies QuestionResultsProps;
    return <div className='relative'>
          <div className='flex'>
            <div ref={ref} className='pt-6 pr-4 pl-2 bg-white grow overflow-x-hidden flex flex-col gap-2'>
              {!question_0.hideInvite && doSafe(inviteLinkMain, l => <div className='mt-[-0.8em] mr-6 self-end flex items-center flex-wrap gap-4 bg-gray-100 pl-3 pr-1 py-1 rounded-lg'>
                    {joinExpand && <div className='shrink-0'>
                        <img className='p-2 size-[min(30em,90vw)]' src={qrImageUrl(l.url)} />
                      </div>}
                    <div className='flex gap-2 items-center'>
                      <div>
                        <div className='text-sm font-semibold opacity-50'>to join, go to</div>
                        <div style={{
                  fontSize: joinExpand ? '2em' : '1.2em'
                }} className='font-bold'>
                          {uri(env.NEXT_PUBLIC_JOIN_URL).domainAndPath}
                        </div>
                      </div>
                      <HiArrowRight className='opacity-30' />
                      <div className='flex flex-col '>
                        <div style={{
                  fontSize: joinExpand ? '3em' : '2em'
                }} className='font-code tracking-widest font-bold uppercase'>
                          {l.id}
                        </div>
                      </div>
                    </div>
                    <div className='self-start'>
                      <ActionIcon onClick={() => setJoinExpand(!joinExpand)}>
                        {joinExpand ? <HiChevronUp /> : <HiChevronDown />}
                      </ActionIcon>
                    </div>
                  </div>)}
              <SErrorBoundary>
                <QuestionResults className='pb-[10em]' {...questionResultsProps} />
              </SErrorBoundary>
            </div>

            {groups && <ResultsPanel showResultsPanel={showResultsPanel} setShowResultsPanel={setShowResultsPanel} toggle={toggle} questionResultsProps={questionResultsProps} forecasterCounts={forecasterCounts} groups={groups} inviteLinkMain={inviteLinkMain} />}

            {!fullscreen && !showResultsPanel && <div className='absolute right-2 top-2'>
                <Tooltip label='configure results and view timeline'>
                  <ActionIcon onClick={() => setShowResultsPanel(true)}>
                    <VscLayoutSidebarRight />
                  </ActionIcon>
                </Tooltip>
              </div>}
          </div>
        </div>;
  }).with({
    forecastsLoading: true
  }, () => <div className='flex flex-col gap-4 justify-center max-w-[20em] mx-auto pt-4 '>
        <Skeleton width='15em' height='2em' radius='md' />
        <Skeleton width='20em' height='6em' radius='md' />
      </div>).otherwise(() => <></>);
};
export const ResultsPanel = t0 => {
  const $ = _c(21);
  const {
    showResultsPanel,
    setShowResultsPanel,
    toggle,
    questionResultsProps,
    forecasterCounts,
    groups,
    inviteLinkMain
  } = t0;
  const {
    workspace_id
  } = usePortal();
  const {
    question_id,
    readonly
  } = useQuestionContext();
  const uTimeline = useTimeline();
  let t1;
  if ($[0] !== question_id) {
    t1 = {
      question_id
    };
    $[0] = question_id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = useQuestionQuery(t1);
  let t3;
  if ($[2] !== t2.data) {
    t3 = pipe(t2.data, _temp13);
    $[2] = t2.data;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const question = t3;
  const {
    query,
    setQuery
  } = useQueryString(ResultsQuery.zQueryNoUpgrade);
  let t4;
  if ($[4] !== forecasterCounts || $[5] !== groups || $[6] !== inviteLinkMain || $[7] !== query || $[8] !== question || $[9] !== questionResultsProps || $[10] !== question_id || $[11] !== readonly || $[12] !== setQuery || $[13] !== setShowResultsPanel || $[14] !== toggle || $[15] !== uTimeline || $[16] !== workspace_id) {
    t4 = styles => <div style={styles} className="flex flex-col items-start gap-6 pl-4 pr-2 py-2 border-l w-[min(30em,50%)]"><div className="flex flex-wrap gap-2 w-full items-center">{!readonly && <SharePublication id={question_id} publications={uTimeline.qForm.values?.publish?.publications ?? {}} query={query} latestForecastAt={uTimeline.latestForecastAt} noteStats={uTimeline.noteStats} showAllNotes={uTimeline.showAllNotes} />}<Button leftSection={<MdFullscreen />} onClick={toggle}>Fullscreen</Button>{question && <Button leftSection={<HiDownload />} onClick={async () => {
          const filename = `${question.title}.${DateTime.utc().toFormat("yyyy_MM_dd_T_HH_mm")}_UTC.zip`;
          const layout = QR.layout(questionResultsProps);
          const blob = await makeQuestionForecastExport(layout);
          downloadBlob(blob, filename);
        }}>Export Data</Button>}<ActionIcon className="ml-auto" onClick={() => {
          setShowResultsPanel(false);
        }}><HiX /></ActionIcon></div><CardCollapsable classNames={{
        root: "w-full",
        container: "pt-2 flex flex-col gap-3"
      }} title="Filters & Display options" variant="card"><InfoLabel>These options apply to the current view and share links and are not saved/shown to others</InfoLabel><div className="flex flex-wrap gap-2"><ProfileFilter counts={forecasterCounts} emptyLabel="forecasters" value={query.forecasters ?? []} onChange={forecasters => {
            setQuery({
              forecasters
            });
          }} hideZeros={true} showClear={true} />{groups && <QuestionFilter groups={groups} value={query.showQuestions ?? []} onChange={qs => setQuery({
            showQuestions: qs
          })} />}<TimeComparisonSelect {...uTimeline} /></div><Chip.Group multiple={true} value={query.show ?? []} onChange={t => setQuery({
          show: ResultsQuery.zShow.parse(Array.isArray(t) ? t : [t])
        })}><div className="flex flex-wrap gap-2">{ResultsQuery.showItemsCurrent.map(t_0 => <Chip key={t_0} value={t_0} checked={query.show?.includes(t_0)} variant="light" size="sm">{match(t_0).with("hideHelp", _temp14).with("hideTitle", _temp15).exhaustive()}</Chip>)}</div></Chip.Group></CardCollapsable>{!readonly && <CardCollapsable classNames={{
        root: "w-full",
        container: "pt-4"
      }} title="Display configuration" variant="card"><DisplayEdit id={question_id} workspace_id={workspace_id} inviteLinkMain={inviteLinkMain} groups={groups as Subqs.SubqGroup[]} /></CardCollapsable>}<SErrorBoundary>{!env.NEXT_PUBLIC_SELF_HOST && <QuestionTimeline {...uTimeline} />}</SErrorBoundary></div>;
    $[4] = forecasterCounts;
    $[5] = groups;
    $[6] = inviteLinkMain;
    $[7] = query;
    $[8] = question;
    $[9] = questionResultsProps;
    $[10] = question_id;
    $[11] = readonly;
    $[12] = setQuery;
    $[13] = setShowResultsPanel;
    $[14] = toggle;
    $[15] = uTimeline;
    $[16] = workspace_id;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== showResultsPanel || $[19] !== t4) {
    t5 = <Transition mounted={showResultsPanel} transition="slide-left" duration={100} timingFunction="ease">{t4}</Transition>;
    $[18] = showResultsPanel;
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
};
export const QuestionFilter = t0 => {
  const $ = _c(11);
  const {
    groups,
    value,
    onChange
  } = t0;
  let t1;
  if ($[0] !== groups) {
    const items = groups.map(_temp17).flat();
    t1 = mapToObj(items, _temp18);
    $[0] = groups;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const md = t1;
  const anySelected = value.length > 0;
  let t2;
  if ($[2] !== onChange) {
    t2 = qs => onChange(qs);
    $[2] = onChange;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== anySelected) {
    t3 = i => <><div className={twMerge("flex gap-2 items-center", i.type != "question" && "pl-2")}>{anySelected && <div className="w-4">{i.selected && <CheckIcon size="1em" className="text-gray-400 w-4" />}</div>}{match(i.type).with("question", _temp19).otherwise(_temp20)}<div className="font-semibold grow">{i.label}</div>{i.count == null ? <></> : <span>` - ${i.count}`</span>}</div>{i.desc && <div className="mt-1">{i.desc}</div>}</>;
    $[4] = anySelected;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== md || $[7] !== t2 || $[8] !== t3 || $[9] !== value) {
    t4 = <ComboMultiInput md={md} value={value} onChange={t2} placeholder="questions" item={t3} disableSort={true} showClear={true} />;
    $[6] = md;
    $[7] = t2;
    $[8] = t3;
    $[9] = value;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
const DisplayEdit = t0 => {
  const $ = _c(8);
  const {
    id,
    workspace_id,
    inviteLinkMain
  } = t0;
  let t1;
  if ($[0] !== id) {
    t1 = {
      question_id: id,
      pick: _temp21
    };
    $[0] = id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    values,
    getInputProps: inProps
  } = useQuestionForm(t1).form;
  const {
    colors
  } = useSafeMantineTheme();
  const {
    workspace: t2
  } = usePortal();
  const {
    advancedUI
  } = t2;
  const cs = values?.colors ?? {};
  const resColor = resultColorFn({
    colors
  });
  const {
    query
  } = useQueryString(zQuestionQuery);
  let t3;
  if ($[2] !== query) {
    t3 = z.object(ResultsQuery.zQueryShape).strip().parse(compactObj({
      ...query,
      compare_ts: isNullOrEmpty(query.compare_ts) ? undefined : query.compare_ts.map(_temp22)
    }));
    $[2] = query;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const queryForLink = t3;
  let t4;
  if ($[4] !== id || $[5] !== queryForLink || $[6] !== workspace_id) {
    t4 = {
      id,
      query: queryForLink,
      type: "liveView",
      workspace_id
    };
    $[4] = id;
    $[5] = queryForLink;
    $[6] = workspace_id;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    data: shareUrl
  } = usePublishedUrl(t4);
  return !values ? <></> : <div className="space-y-6">{advancedUI && <div className="flex flex-col gap-2 bg-primary/5 p-2 border rounded-lg"><Switch label="Results viewer access" size="md" {...inProps("liveView", {
        type: "checkbox"
      })} /><InfoLabel><div>This allows users with the <b>result viewer</b> role to view this question result. Usefull as an alternative to publishing the results when you require users to log in to be able to view the results.</div></InfoLabel>{values.liveView && <>{shareUrl && <CopyLink url={shareUrl} />}</>}</div>}<div className="flex flex-wrap gap-2">{questionColors.filter(_temp23).map(c_0 => {
        const {
          onChange,
          ...p
        } = inProps(`colors.${c_0}`);
        return <SColorInput key={c_0} {...p} onChange={v => {
          if (v == resColor(c_0)) {
            return;
          }
          onChange(v);
        }} {...{
          label: capitalize(c_0),
          value: cs[c_0] ?? resColor(c_0)
        }} />;
      })}</div>{inviteLinkMain && <Switch label="Hide invite code" size="md" {...inProps("hideInvite", {
      type: "checkbox"
    })} />}<MasonryGrid colWidth="13em" className="gap-2"><Tooltip multiline={true} label="For conditional forecasts the relative values of conditional forecasts are emphasized in the viz"><div><Switch size="md" {...inProps("chart.relative", {
            type: "checkbox"
          })} label="Relative emphasis" /></div></Tooltip><Tooltip label="Show % out of the configured question bounds"><div><Switch size="md" {...inProps("chart.showTails", {
            type: "checkbox"
          })} label="% out of bounds" /></div></Tooltip></MasonryGrid>{values.aggVersion == "1" && <Tooltip label={`Currently using version ${values.aggVersion}. Upgrading will show higher fidelity quantity aggregation`}><div><Button size="sm" leftSection={<HiArrowCircleUp />} onClick={() => inProps("aggVersion").onChange("2")}>Use latest aggregation method</Button></div></Tooltip>}{values.hideSubTitles && <Tooltip className="max-w-[30em]" label="Hide/show sub-titles. In older versions the sub-title was not displayed. Once shown this option will disappear as you should consider the sub-title part of the title"><div><Switch label="Hide sub-titles" size="md" {...inProps("hideSubTitles", {
          type: "checkbox"
        })} /></div></Tooltip>}</div>;
};
function _temp13(d) {
  return d?.ok ? d.cfg : undefined;
}
function _temp14(_) {
  return "hide help";
}
function _temp15(__0) {
  return "hide titles";
}
function _temp16(s_0) {
  return {
    id: s_0.id,
    label: s_0.cfg.title,
    type: s_0.cfg.subquestionType
  };
}
function _temp17(s) {
  return [{
    id: s.id,
    label: s.cfg.title,
    type: s.isRootQuestion ? "question" as const : s.cfg.subquestionType
  }, ...s.subqs.map(_temp16)];
}
function _temp18(s_1) {
  return [s_1.id, s_1];
}
function _temp19() {
  return <MdHome />;
}
function _temp20(t) {
  return <SubqTypeIcon type={t} variant="icon" className="" />;
}
function _temp21(cfg) {
  return pick(cfg, ["publish", "colors", "hideSubTitles", "aggVersion", "liveView", "chart"]);
}
function _temp22(ts) {
  return ts.toString();
}
function _temp23(c) {
  return !"group".includes(c);
}